<script setup lang="ts">
import { faPlane } from '@fortawesome/pro-regular-svg-icons';
import type TripSegment from '@itinerary/trip/TripSegment';
import { VehicleType } from '@itinerary/trip/types';
import { busSide, taxiSide, trainSide } from '@static/customIcons';

interface IProps {
    segment: TripSegment;
    showDuration?: boolean;
    fullWidth?: boolean;
}
const { segment, showDuration, fullWidth } = defineProps<IProps>();

const duration = computed(() => formatTripDuration(segment.duration * 1000));
const stops = computed(() =>
    segment.trips.length > 1 ? segment.trips.length - 1 : 0
);

const numberOFDomesticStops = computed(() =>
    segment.trips.reduce((prev, next) => prev + next.stops, 0)
);

const icon = computed(() => {
    switch (segment.trips[0].vehicleType) {
        case VehicleType.Plane:
            return {
                icon: faPlane,
                rotate: '180' as const,
            };
        case VehicleType.Train:
            return {
                icon: trainSide,
            };
        case VehicleType.Bus:
            return {
                icon: busSide,
            };
        case VehicleType.Taxi:
            return {
                icon: taxiSide,
            };
        default:
            return {
                icon: undefined,
            };
    }
});

const allStops = computed(() => numberOFDomesticStops.value + stops.value);
</script>

<template>
    <div
        class="route-view-container"
        :style="{ maxWidth: fullWidth ? 'unset' : '150px' }"
    >
        <div v-if="showDuration" class="duration">
            {{ duration }}
        </div>
        <div class="route-container">
            <div class="circle" />
            <div class="stops">
                <div v-if="allStops > 0" class="icon-container">
                    <div class="circle stops-circle" />
                    <mr-typography
                        type="span"
                        size="small"
                        weight="500"
                        color="var(--cool-gray-500)"
                        class="title"
                        align="center"
                    >
                        {{ allStops }} توقف
                    </mr-typography>
                </div>
            </div>
            <mr-icon
                v-if="icon.icon"
                :icon="icon.icon"
                :rotation="icon.rotate"
                size="small"
                color="var(--cool-gray-400)"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.route-view-container {
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 32px;
    @include mobile-only {
        flex: 1;
        width: auto;
    }
    .duration {
        position: absolute;
        top: -18px;
        @include tg-s($w5, usePalette($p-cool-gray, $p-500));
        text-align: center;
    }
    .route-container {
        flex-shrink: 0;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 20px;
        width: 100%;
        .circle {
            width: 6px;
            height: 6px;
            background: usePalette($p-cool-gray, 500);
            margin: 4px;
            border-radius: 100%;
            flex-shrink: 0;
            &.stops-circle {
                position: absolute;
                top: -6px;
                left: -9px;
            }
        }
        .stops {
            width: 100%;
            height: 100%;
            display: flex;
            background-image: url('/assets/files/card/dot.svg');
            background-position: top;
            background-repeat: repeat-x;
            justify-content: space-around;
            margin-top: 17px;
            .icon-container {
                position: relative;
                .title {
                    top: 4px;
                    position: absolute;
                    left: 50%;
                    min-width: 40px;
                    transform: translateX(-50%);
                }
            }
        }
    }
}
</style>
